/**
 * Utility Functions for Contacts
 *
 * This module provides helper functions for contact-related operations,
 * including formatting, validation, and data transformations.
 */

import type { Contact } from './types';
import { RELATIONSHIP_OPTIONS } from '@/lib/constants/relationships';

// ============================================================================
// Contact Type Detection
// ============================================================================

/**
 * Determine if a contact is an organisation (vs individual)
 */
export function isOrganisationContact(
  contact: Partial<Contact> | null | undefined
): boolean {
  if (!contact) return false;
  return Boolean(contact.organisationName?.trim());
}

/**
 * Get the contact type based on data
 */
export function getContactType(
  contact: Partial<Contact> | null | undefined
): 'individual' | 'organisation' {
  return isOrganisationContact(contact) ? 'organisation' : 'individual';
}

/**
 * Minimum required name for a contact to be considered complete — the single
 * source of truth shared by the AI-import/Scribe/intake review cards and the
 * manual ContactFormCard (LF-5427). An organisation is valid with just its
 * name. An LPR (executor/administrator) is named on legal documents, so needs
 * a full first + last name; any other individual (beneficiary, relative,
 * etc.) only needs one of first/last name to identify them.
 */
export function hasRequiredContactName(
  contact: Record<string, unknown>
): boolean {
  const hasOrganisation = Boolean(
    (contact.organisationName as string | null | undefined)?.trim()
  );
  if (hasOrganisation) return true;

  const first = (contact.firstName as string | undefined)?.trim();
  const last = (contact.lastName as string | undefined)?.trim();
  if (contact.isLegalPersonalRepresentative) {
    return Boolean(first && last);
  }
  return Boolean(first || last);
}

// ============================================================================
// Formatting Functions
// ============================================================================

/**
 * Whether the contact has been flagged as deceased (e.g. a predeceased
 * beneficiary). Drives the "(deceased)" name suffix and the entitlements-page
 * warning banner. The matter's own decedent is stored on `matter.decedent*`,
 * not as a contact — this is for *other* people on the matter who have died.
 *
 * The authoritative signal is the first-class `isDeceased` flag; a non-null
 * `dateOfDeath` is a fallback for legacy records / payloads that carry only the
 * date (so a deceased contact still reads as such even without the boolean).
 */
export function isContactDeceased(
  contact: Partial<Contact> | null | undefined
): boolean {
  return Boolean(contact?.isDeceased) || Boolean(contact?.dateOfDeath);
}

/**
 * Whether the contact is flagged as a minor. The backend persists minor status
 * as `isOfAge` (`false` = minor); `null`/`undefined` means "unknown", so only
 * an explicit `false` counts — an unknown-age beneficiary stays in the active
 * verify/pay flow rather than being hidden. Drives the dedicated, non-actionable
 * "Minors" section on the Beneficiaries tab, mirroring the deceased treatment:
 * a minor can't be identity/bank verified or sign a declaration (their share is
 * typically held in trust). The UI-only `isMinor` field is a form convenience
 * (inverse of `isOfAge`) and is not persisted, so the read path keys off
 * `isOfAge`.
 */
export function isContactMinor(
  contact: Partial<Contact> | null | undefined
): boolean {
  return contact?.isOfAge === false;
}

/**
 * Get contact display name - handles both individuals and organisations.
 * Appends " (deceased)" when the contact is flagged deceased so lawyers can
 * spot a predeceased beneficiary at a glance everywhere the name renders.
 */
export function getContactDisplayName(
  contact: Partial<Contact> | null | undefined
): string {
  if (!contact) return 'New Contact';

  let name: string;
  if (contact.organisationName?.trim()) {
    name = contact.organisationName.trim();
  } else {
    const firstName = contact.firstName?.trim() || '';
    const middleName = contact.middleName?.trim() || '';
    const lastName = contact.lastName?.trim() || '';
    const nameParts = [firstName, middleName, lastName].filter(Boolean);
    name = nameParts.join(' ') || 'New Contact';
  }

  if (isContactDeceased(contact) && name !== 'New Contact') {
    return `${name} (deceased)`;
  }
  return name;
}

// ============================================================================
// Search and Filter Functions
// ============================================================================

/**
 * Filter contacts by type.
 */
export function filterContactsByType(
  contacts: Contact[],
  type: 'beneficiary' | 'lpr' | 'other'
): Contact[] {
  switch (type) {
    case 'beneficiary':
      return contacts.filter((c) => c.isBeneficiary);
    case 'lpr':
      return contacts.filter((c) => c.isLegalPersonalRepresentative);
    case 'other':
      return contacts.filter(
        (c) => !c.isBeneficiary && !c.isLegalPersonalRepresentative
      );
    default:
      return contacts;
  }
}

// ============================================================================
// Data Transformation Functions
// ============================================================================

const RELATIONSHIP_TYPE_ORDER: Record<string, number> = Object.fromEntries(
  RELATIONSHIP_OPTIONS.map((option, index) => [option.value, index])
);

/**
 * Where a contact's relationship-to-deceased falls in the canonical
 * RELATIONSHIP_OPTIONS order (LF-5711 — Child must sort before Grandchild).
 * An unset or unrecognised relationship sorts after every named type.
 */
function relationshipTypeRank(contact: Contact): number {
  const relationship = contact.relationshipToDeceased;
  if (!relationship) return RELATIONSHIP_OPTIONS.length;
  return RELATIONSHIP_TYPE_ORDER[relationship] ?? RELATIONSHIP_OPTIONS.length;
}

/**
 * Sort contacts by importance, grouped by contact type.
 *
 * Order: LPRs → individual beneficiaries → organisation beneficiaries →
 * everyone else. Within each group, contacts are further ordered by
 * relationship-to-deceased (per RELATIONSHIP_OPTIONS' canonical order, e.g.
 * Child before Grandchild — LF-5711), then A→Z by display name. The
 * individual-before-organisation split keeps organisation beneficiaries (e.g.
 * charities) grouped together at the end rather than sorting alphabetically in
 * among the individual beneficiaries.
 */
export function sortContactsByImportance(contacts: Contact[]): Contact[] {
  const groupRank = (contact: Contact): number => {
    if (contact.isLegalPersonalRepresentative) return 0;
    if (contact.isBeneficiary) return isOrganisationContact(contact) ? 2 : 1;
    return 3;
  };

  return [...contacts].sort((a, b) => {
    const rankDiff = groupRank(a) - groupRank(b);
    if (rankDiff !== 0) return rankDiff;

    const relationshipDiff = relationshipTypeRank(a) - relationshipTypeRank(b);
    if (relationshipDiff !== 0) return relationshipDiff;

    return getContactDisplayName(a).localeCompare(getContactDisplayName(b));
  });
}

// ============================================================================
// Cache Management
// ============================================================================

/**
 * Generate cache key for contacts query
 */
export function getContactsCacheKey(matterId: number): (string | number)[] {
  return ['contacts', 'list', matterId];
}

/**
 * Generate cache key for single contact query
 */
export function getContactCacheKey(
  matterId: number,
  contactId: number
): (string | number)[] {
  return ['contacts', 'item', matterId, contactId];
}

// ============================================================================
// Export Functions
// ============================================================================

/**
 * Export contacts to CSV format
 */
export function exportContactsToCSV(contacts: Contact[]): string {
  const headers = [
    'Type',
    'Display Name',
    'First Name',
    'Last Name',
    'Organisation Name',
    'Email',
    'Legal Personal Representative',
    'Beneficiary'
  ];

  const rows = contacts.map((contact) => [
    isOrganisationContact(contact) ? 'Organisation' : 'Individual',
    getContactDisplayName(contact),
    contact.firstName || '',
    contact.lastName || '',
    contact.organisationName || '',
    contact.email || '',
    contact.isLegalPersonalRepresentative ? 'Yes' : 'No',
    contact.isBeneficiary ? 'Yes' : 'No'
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))
  ].join('\n');

  return csvContent;
}
